import { useCallback, useState } from "react";
import type { QaComputer, QaNeedsYou, QaRunnerGroup, QaTarget } from "../../api/qaOrchestrator";
import { useConfirm, type ConfirmOptions } from "../ConfirmDialog";

// What the sections of Manage -> QA (AdminQaPage) share.

export type SetError = (e: string | null) => void;

/** The built-in runner group the MCP server's drafts and debug sessions use; the orchestrator refuses to delete it. */
export const DRAFT_GROUP = "draft-testing";

/** Every write on this page: run it, then reload, and show what went wrong. */
export function useAction(reload: () => Promise<void>, setError: SetError) {
  const [busy, setBusy] = useState(false);
  const run = useCallback(async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await reload();
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  }, [reload, setError]);
  const [confirm, dialog] = useConfirm();
  /** Asks first (the app's dialog, not the browser's), then runs. */
  const confirmThen = useCallback(async (options: ConfirmOptions, fn: () => Promise<unknown>) => {
    if (await confirm({ danger: true, ...options })) await run(fn);
  }, [confirm, run]);
  return { busy, run, confirmThen, dialog };
}

// Computer states whose wire name does not read well; the rest show as they are.
// A paused runner is open for a person to work in its QA account (Ctrl+Alt+Q at the computer, or Pause here).
const STATE_LABEL: Record<string, string> = { paused: "Maintenance", unavailable: "Unavailable", waiting_for_idle: "Waiting for idle" };

export function StatePill({ state }: { state: string }) {
  return <span className={`qo-pill qo-pill-${state}`}>{STATE_LABEL[state] ?? state}</span>;
}

/** What a computer needs from a person (the orchestrator's needsYou): actions first and prominent, info muted. */
export function NeedsYouList({ items }: { items?: QaNeedsYou[] }) {
  if (!items?.length) return null;
  const sorted = [...items.filter((i) => i.level === "action"), ...items.filter((i) => i.level !== "action")];
  return (
    <ul className="qo-needs" aria-label="Needs you">
      {sorted.map((i) => (
        <li key={i.id} className={`qo-needs-${i.level}`} data-level={i.level}>
          <span>{i.level === "action" ? "Needs you: " : ""}{i.message}</span>
          {i.level === "action" && i.hint && <small>{i.hint}</small>}
        </li>
      ))}
    </ul>
  );
}

/** "a, b , c" -> ["a", "b", "c"] */
export const splitList = (s: string) => s.split(",").map((x) => x.trim()).filter(Boolean);

export function TargetEditor({ value, computers, groups, onChange, disabled }: { value: QaTarget; computers: QaComputer[]; groups: QaRunnerGroup[]; onChange: (t: QaTarget) => void; disabled?: boolean }) {
  const kind = value.group ? "group" : value.computers?.length ? "computers" : "any";
  return (
    <div className="qo-target">
      <select disabled={disabled} value={kind} onChange={(e) => {
        const k = e.target.value;
        if (k === "any") onChange({});
        if (k === "group" && groups[0]) onChange({ group: groups[0].id });
        if (k === "computers" && computers[0]) onChange({ computers: [computers[0].name] });
      }}>
        <option value="any">Any runner</option>
        <option value="group" disabled={groups.length === 0}>Runner group</option>
        <option value="computers" disabled={computers.length === 0}>Specific runners</option>
      </select>
      {kind === "group" && (
        <select disabled={disabled} value={value.group} onChange={(e) => onChange({ group: e.target.value })}>
          {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
      )}
      {kind === "computers" && (
        <span className="qo-checks qo-checks-inline">
          {computers.map((c) => (
            <label key={c.id}>
              <input type="checkbox" disabled={disabled} checked={!!value.computers?.includes(c.name) || !!value.computers?.includes(c.id)} onChange={(e) => {
                const rest = (value.computers ?? []).filter((x) => x !== c.name && x !== c.id);
                const next = e.target.checked ? [...rest, c.name] : rest;
                onChange(next.length ? { computers: next } : {});
              }} />
              {c.name}
            </label>
          ))}
        </span>
      )}
    </div>
  );
}
